/* ============================================================
   Teja Home Theme — Main JavaScript
   Premium Indian Home & Kitchen E-commerce
   ============================================================ */

(function() {
  'use strict';

  /* ---------- Utility Functions ---------- */
  const $ = (selector, parent = document) => parent.querySelector(selector);
  const $$ = (selector, parent = document) => [...parent.querySelectorAll(selector)];

  function debounce(fn, delay = 250) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  function formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }

  /* ---------- Sticky Header ---------- */
  function initStickyHeader() {
    const header = $('.site-header');
    if (!header) return;

    let lastScroll = 0;
    const scrollThreshold = 50;

    window.addEventListener('scroll', () => {
      const currentScroll = window.pageYOffset;

      if (currentScroll > scrollThreshold) {
        header.classList.add('scrolled');
      } else {
        header.classList.remove('scrolled');
      }

      lastScroll = currentScroll;
    }, { passive: true });
  }

  /* ---------- Mobile Menu ---------- */
  function initMobileMenu() {
    const toggle = $('.header__menu-toggle');
    const menu = $('.mobile-menu');
    const close = $('.mobile-menu__close');
    const overlay = $('.overlay');

    if (!toggle || !menu) return;

    function openMenu() {
      menu.classList.add('open');
      document.body.classList.add('menu-open');
      if (overlay) overlay.classList.add('active');
    }

    function closeMenu() {
      menu.classList.remove('open');
      document.body.classList.remove('menu-open');
      if (overlay) overlay.classList.remove('active');
    }

    toggle.addEventListener('click', openMenu);
    if (close) close.addEventListener('click', closeMenu);
    if (overlay) overlay.addEventListener('click', closeMenu);

    // Accordion sub-menus
    $$('.mobile-menu__link[data-toggle]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const item = link.closest('.mobile-menu__item');
        item.classList.toggle('open');
      });
    });
  }

  /* ---------- Hero Slideshow ---------- */
  function initHeroSlideshow() {
    const slideshow = $('.hero-slideshow');
    if (!slideshow) return;

    const track = $('.hero-slideshow__track', slideshow);
    const slides = $$('.hero-slideshow__slide', slideshow);
    const dots = $$('.hero-slideshow__dot', slideshow);
    const prevBtn = $('.hero-slideshow__nav--prev', slideshow);
    const nextBtn = $('.hero-slideshow__nav--next', slideshow);

    if (slides.length <= 1) return;

    let current = 0;
    let autoplayTimer;
    const autoplayDelay = 5000;

    function goTo(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;

      current = index;
      track.style.transform = `translateX(-${current * 100}%)`;

      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === current);
      });
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }

    function startAutoplay() {
      stopAutoplay();
      autoplayTimer = setInterval(next, autoplayDelay);
    }

    function stopAutoplay() {
      clearInterval(autoplayTimer);
    }

    if (prevBtn) prevBtn.addEventListener('click', () => { prev(); startAutoplay(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { next(); startAutoplay(); });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { goTo(i); startAutoplay(); });
    });

    // Touch swipe
    let touchStartX = 0;
    let touchEndX = 0;

    track.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
      stopAutoplay();
    }, { passive: true });

    track.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        diff > 0 ? next() : prev();
      }
      startAutoplay();
    }, { passive: true });

    startAutoplay();
  }

  /* ---------- Product Carousels ---------- */
  function initProductCarousels() {
    $$('.products-carousel').forEach(carousel => {
      const track = $('.products-carousel__track', carousel);
      const prevBtn = $('.carousel__nav--prev', carousel);
      const nextBtn = $('.carousel__nav--next', carousel);

      if (!track) return;

      const items = track.children;
      if (items.length === 0) return;

      let scrollAmount = 0;

      function getItemWidth() {
        const style = window.getComputedStyle(track);
        const gap = parseInt(style.gap) || 24;
        return items[0].offsetWidth + gap;
      }

      function getVisibleItems() {
        const width = carousel.offsetWidth;
        const itemWidth = items[0].offsetWidth;
        return Math.floor(width / itemWidth);
      }

      function scrollTo(amount) {
        const maxScroll = track.scrollWidth - carousel.offsetWidth;
        scrollAmount = Math.max(0, Math.min(amount, maxScroll));
        track.style.transform = `translateX(-${scrollAmount}px)`;
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          scrollTo(scrollAmount - getItemWidth() * Math.max(1, getVisibleItems() - 1));
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          scrollTo(scrollAmount + getItemWidth() * Math.max(1, getVisibleItems() - 1));
        });
      }

      // Touch swipe
      let startX = 0;
      let startScroll = 0;
      let isDragging = false;

      track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].pageX;
        startScroll = scrollAmount;
        isDragging = true;
      }, { passive: true });

      track.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        const diff = startX - e.touches[0].pageX;
        scrollTo(startScroll + diff);
      }, { passive: true });

      track.addEventListener('touchend', () => {
        isDragging = false;
        // Snap to nearest item
        const itemWidth = getItemWidth();
        scrollTo(Math.round(scrollAmount / itemWidth) * itemWidth);
      }, { passive: true });
    });
  }

  /* ---------- Review Carousel ---------- */
  function initReviewCarousel() {
    $$('.reviews-carousel').forEach(carousel => {
      const track = $('.reviews-carousel__track', carousel);
      const prevBtn = $('.carousel__nav--prev', carousel);
      const nextBtn = $('.carousel__nav--next', carousel);

      if (!track) return;

      let scrollAmount = 0;

      function getCardWidth() {
        const card = track.children[0];
        if (!card) return 300;
        const style = window.getComputedStyle(track);
        const gap = parseInt(style.gap) || 24;
        return card.offsetWidth + gap;
      }

      function scroll(direction) {
        const maxScroll = track.scrollWidth - carousel.offsetWidth;
        scrollAmount = Math.max(0, Math.min(scrollAmount + direction * getCardWidth(), maxScroll));
        track.style.transform = `translateX(-${scrollAmount}px)`;
      }

      if (prevBtn) prevBtn.addEventListener('click', () => scroll(-1));
      if (nextBtn) nextBtn.addEventListener('click', () => scroll(1));

      // Touch swipe
      let startX = 0;
      track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].pageX;
      }, { passive: true });

      track.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].pageX;
        if (Math.abs(diff) > 50) {
          scroll(diff > 0 ? 1 : -1);
        }
      }, { passive: true });
    });
  }

  /* ---------- Product Gallery (Product Page) ---------- */
  function initProductGallery() {
    const gallery = $('.product-gallery');
    if (!gallery) return;

    const mainImage = $('.product-gallery__main-image', gallery);
    const thumbs = $$('.product-gallery__thumb', gallery);

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const newSrc = thumb.dataset.fullImage || thumb.querySelector('img').src;
        mainImage.src = newSrc;

        thumbs.forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
      });
    });
  }

  /* ---------- Product Tabs ---------- */
  function initProductTabs() {
    $$('.product-tabs').forEach(tabContainer => {
      const tabs = $$('.product-tabs__tab', tabContainer);
      const panels = $$('.product-tabs__panel', tabContainer);

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => {
          tabs.forEach(t => t.classList.remove('active'));
          panels.forEach(p => p.classList.remove('active'));

          tab.classList.add('active');
          panels[index].classList.add('active');
        });
      });
    });
  }

  /* ---------- Variant Selector ---------- */
  function initVariantSelector() {
    $$('.variant-option').forEach(option => {
      option.addEventListener('click', function() {
        if (this.classList.contains('unavailable')) return;

        const group = this.closest('.variant-options');
        $$('.variant-option', group).forEach(opt => opt.classList.remove('selected'));
        this.classList.add('selected');

        // Update hidden select if exists
        const select = this.closest('.variant-group')?.querySelector('select');
        if (select) {
          select.value = this.dataset.value;
          select.dispatchEvent(new Event('change'));
        }
      });
    });
  }

  /* ---------- Quantity Selector ---------- */
  function initQuantitySelectors() {
    $$('.quantity-selector').forEach(selector => {
      const input = $('input', selector);
      const minusBtn = $('.quantity-selector__btn--minus', selector);
      const plusBtn = $('.quantity-selector__btn--plus', selector);

      if (!input) return;

      const min = parseInt(input.min) || 1;
      const max = parseInt(input.max) || 99;

      function update(value) {
        value = Math.max(min, Math.min(max, value));
        input.value = value;
        input.dispatchEvent(new Event('change'));
      }

      if (minusBtn) {
        minusBtn.addEventListener('click', () => update(parseInt(input.value) - 1));
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', () => update(parseInt(input.value) + 1));
      }

      input.addEventListener('change', () => {
        let val = parseInt(input.value);
        if (isNaN(val) || val < min) val = min;
        if (val > max) val = max;
        input.value = val;
      });
    });
  }

  /* ---------- AJAX Add to Cart ---------- */
  function initAddToCart() {
    $$('form[action="/cart/add"]').forEach(form => {
      form.addEventListener('submit', async function(e) {
        e.preventDefault();

        const submitBtn = $('[type="submit"]', form);
        const originalText = submitBtn?.textContent;

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.textContent = 'Adding...';
        }

        try {
          const formData = new FormData(form);
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) throw new Error('Failed to add to cart');

          const item = await response.json();

          // Update cart count
          updateCartCount();

          // Show toast
          showToast(`✓ ${item.title} added to cart!`);

        } catch (err) {
          showToast('Could not add to cart. Please try again.', 'error');
        } finally {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
          }
        }
      });
    });
  }

  /* ---------- Update Cart Count ---------- */
  async function updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      $$('.header__cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count > 0 ? 'flex' : 'none';
      });
    } catch (err) {
      // Silently fail
    }
  }

  /* ---------- Toast Notifications ---------- */
  function showToast(message, type = 'success') {
    // Remove existing toasts
    $$('.toast').forEach(t => t.remove());

    const toast = document.createElement('div');
    toast.className = 'toast';
    if (type === 'error') {
      toast.style.background = 'var(--color-sale)';
    }
    toast.innerHTML = `
      <span>${message}</span>
      <button class="toast__close" aria-label="Close">✕</button>
    `;

    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });

    // Auto-hide
    const hideTimer = setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 400);
    }, 3500);

    // Close button
    $('.toast__close', toast)?.addEventListener('click', () => {
      clearTimeout(hideTimer);
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 400);
    });
  }

  /* ---------- Scroll Reveal Animations ---------- */
  function initScrollReveal() {
    const elements = $$('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    if (elements.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    elements.forEach(el => observer.observe(el));
  }

  /* ---------- Announcement Bar Rotation ---------- */
  function initAnnouncementRotation() {
    const bar = $('.announcement-bar--rotating');
    if (!bar) return;

    const messages = $$('.announcement-bar__message', bar);
    if (messages.length <= 1) return;

    let current = 0;

    setInterval(() => {
      messages[current].style.opacity = '0';
      current = (current + 1) % messages.length;
      messages[current].style.opacity = '1';
    }, 4000);
  }

    /* ---------- Live Predictive Search Dropdown ---------- */
  function initSearch() {
    const input = document.getElementById('HeaderSearchInput');
    const container = document.getElementById('HeaderSearchResults');
    if (!input || !container) return;

    const performSearch = debounce(async (query) => {
      query = query.trim();
      if (query.length < 2) {
        container.classList.remove('active');
        container.innerHTML = '';
        return;
      }

      try {
        container.innerHTML = '<div style="padding:1rem;text-align:center;font-size:0.85rem;color:var(--color-text-light);">Searching for "' + query + '"...</div>';
        container.classList.add('active');

        const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
        if (!res.ok) throw new Error('Search request failed');
        
        const data = await res.json();
        const products = data.resources?.results?.products || [];

        if (products.length === 0) {
          container.innerHTML = `
            <div style="padding:1.2rem;text-align:center;">
              <div style="font-size:1.5rem;margin-bottom:0.25rem;">🔍</div>
              <div style="font-size:0.875rem;font-weight:600;color:var(--color-text);">No products found for "${query}"</div>
              <div style="font-size:0.75rem;color:var(--color-text-light);margin-top:0.25rem;">Try searching for cookware, storage, or decor</div>
            </div>`;
          return;
        }

        let html = '';
        products.forEach(p => {
          const imgUrl = p.image || p.featured_image?.url || 'https://images.unsplash.com/photo-1584992236310-6edddc08acff?auto=format&fit=crop&w=150&q=80';
          const price = formatMoney(p.price * 100);
          
          html += `
            <a href="${p.url}" class="search-result-item">
              <img src="${imgUrl}" alt="${p.title}" class="search-result-item__image" width="48" height="48" loading="lazy">
              <div class="search-result-item__info">
                <div class="search-result-item__title">${p.title}</div>
                <div class="search-result-item__price">${price}</div>
              </div>
            </a>
          `;
        });

        html += `
          <div class="search-results-footer">
            <a href="/search?q=${encodeURIComponent(query)}">View all results for "${query}" →</a>
          </div>
        `;

        container.innerHTML = html;
        container.classList.add('active');
      } catch (err) {
        container.innerHTML = `
          <div class="search-results-footer" style="border:none;padding:0.75rem 0;">
            <a href="/search?q=${encodeURIComponent(query)}">Press Enter to search for "${query}" →</a>
          </div>`;
      }
    }, 200);

    input.addEventListener('input', (e) => performSearch(e.target.value));
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && container.children.length > 0) {
        container.classList.add('active');
      }
    });

    document.addEventListener('click', (e) => {
      if (!input.contains(e.target) && !container.contains(e.target)) {
        container.classList.remove('active');
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        container.classList.remove('active');
      }
    });
  }

  /* ---------- Newsletter Form ---------- */
  function initNewsletterForm() {
    $$('.newsletter__form').forEach(form => {
      form.addEventListener('submit', function(e) {
        // Let Shopify handle the form submission
        // Just add some UI feedback
        const btn = $('button', form);
        if (btn) {
          const originalText = btn.textContent;
          btn.textContent = 'Subscribing...';
          btn.disabled = true;

          setTimeout(() => {
            btn.textContent = originalText;
            btn.disabled = false;
          }, 3000);
        }
      });
    });
  }

  /* ---------- Free Shipping Progress Bar ---------- */
  function initFreeShippingBar() {
    const bar = $('.free-shipping-bar');
    if (!bar) return;

    const fill = $('.free-shipping-bar__fill', bar);
    const text = $('.free-shipping-bar__text', bar);

    if (!fill) return;

    const threshold = parseInt(bar.dataset.threshold) || 499;

    async function update() {
      try {
        const res = await fetch('/cart.js');
        const cart = await res.json();
        const total = cart.total_price / 100; // Convert from paisa
        const percentage = Math.min((total / threshold) * 100, 100);

        fill.style.width = percentage + '%';

        if (total >= threshold) {
          text.innerHTML = '🎉 You\'ve unlocked <strong>FREE shipping!</strong>';
        } else {
          const remaining = threshold - total;
          text.innerHTML = `Add <strong>₹${remaining.toLocaleString('en-IN')}</strong> more for <strong>FREE shipping!</strong>`;
        }
      } catch (err) {
        // Silently fail
      }
    }

    update();
  }

  /* ---------- Lazy Loading Images ---------- */
  function initLazyLoad() {
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading supported
      $$('img[data-src]').forEach(img => {
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      });
    } else {
      // Fallback with IntersectionObserver
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            observer.unobserve(img);
          }
        });
      });

      $$('img[data-src]').forEach(img => observer.observe(img));
    }
  }

  /* ---------- Cart Item Remove ---------- */
  function initCartItemRemove() {
    $$('.cart-item__remove').forEach(btn => {
      btn.addEventListener('click', async function(e) {
        e.preventDefault();
        const lineIndex = this.dataset.line;
        const item = this.closest('.cart-item');

        try {
          item.style.opacity = '0.5';
          
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              line: parseInt(lineIndex),
              quantity: 0
            })
          });

          // Reload the page to update cart
          window.location.reload();
        } catch (err) {
          item.style.opacity = '1';
          showToast('Could not remove item. Please try again.', 'error');
        }
      });
    });
  }

  /* ---------- Initialize Everything ---------- */
  function init() {
    initStickyHeader();
    initMobileMenu();
    initHeroSlideshow();
    initProductCarousels();
    initReviewCarousel();
    initProductGallery();
    initProductTabs();
    initVariantSelector();
    initQuantitySelectors();
    initAddToCart();
    initScrollReveal();
    initAnnouncementRotation();
    initSearch();
    initNewsletterForm();
    initFreeShippingBar();
    initLazyLoad();
    initCartItemRemove();
    updateCartCount();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
